import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  MessageSquare, Search, Filter, Send, User, Clock, Tag,
  Facebook, Instagram, Twitter, Youtube, MessageCircle, Mail,
  Globe, MoreVertical, ChevronRight, RefreshCw, UserPlus,
  CheckCircle, AlertCircle, Inbox, Settings, X, Phone
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Channel icons mapping
const channelIcons = {
  whatsapp: MessageCircle,
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
  website_chat: Globe,
  website_form: Globe,
  email: Mail,
  sms: Phone
};

const channelColors = {
  whatsapp: 'bg-green-500',
  facebook: 'bg-blue-600',
  instagram: 'bg-pink-500',
  twitter: 'bg-sky-500',
  youtube: 'bg-red-600',
  website_chat: 'bg-indigo-500',
  website_form: 'bg-purple-500',
  email: 'bg-amber-500',
  sms: 'bg-teal-500'
};

const statusColors = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  pending: 'bg-orange-100 text-orange-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-slate-100 text-slate-700'
};

const priorityColors = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600'
};

const OmnichannelInbox = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  
  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [agents, setAgents] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Message input
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Dialogs
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [channels, setChannels] = useState([]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const params = new URLSearchParams({ token });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (channelFilter !== 'all') params.append('channel', channelFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/omnichannel/inbox?${params}`);
      setConversations(response.data.conversations || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, channelFilter, searchQuery]);

  // Fetch agents
  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/omnichannel/agents?token=${token}`);
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  // Fetch channels
  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/omnichannel/channels?token=${token}`);
      setChannels(response.data || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  // Fetch single conversation
  const fetchConversation = async (id) => {
    try {
      const response = await axios.get(`${API}/omnichannel/conversations/${id}?token=${token}`);
      setSelectedConversation(response.data);
    } catch (error) {
      console.error('Error fetching conversation:', error);
      toast.error('Failed to load conversation');
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchAgents();
    fetchChannels();
  }, [fetchConversations]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await axios.post(
        `${API}/omnichannel/conversations/${selectedConversation.id}/messages?token=${token}`,
        { content: newMessage, content_type: 'text' }
      );
      
      setNewMessage('');
      fetchConversation(selectedConversation.id);
      fetchConversations();
      toast.success('Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Update conversation status
  const handleUpdateStatus = async (status) => {
    if (!selectedConversation) return;
    
    try {
      await axios.put(
        `${API}/omnichannel/conversations/${selectedConversation.id}?token=${token}`,
        { status }
      );
      
      fetchConversation(selectedConversation.id);
      fetchConversations();
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Assign conversation
  const handleAssign = async (agentId) => {
    if (!selectedConversation) return;
    
    try {
      await axios.post(
        `${API}/omnichannel/conversations/${selectedConversation.id}/assign?token=${token}`,
        { assigned_to: agentId }
      );
      
      setShowAssignDialog(false);
      fetchConversation(selectedConversation.id);
      fetchConversations();
      toast.success('Conversation assigned');
    } catch (error) {
      toast.error('Failed to assign conversation');
    }
  };

  // Create lead from conversation
  const handleCreateLead = async () => {
    if (!selectedConversation) return;
    
    try {
      const response = await axios.post(
        `${API}/omnichannel/conversations/${selectedConversation.id}/create-lead?token=${token}`,
        {}
      );
      
      if (response.data.is_new) {
        toast.success('New lead created!');
      } else {
        toast.info('Linked to existing contact');
      }
      
      fetchConversation(selectedConversation.id);
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric' });
  };

  const ChannelIcon = ({ channel, size = 16 }) => {
    const Icon = channelIcons[channel] || MessageSquare;
    return <Icon size={size} />;
  };

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col" data-testid="omnichannel-inbox">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
            <Inbox className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Unified Inbox</h1>
            <p className="text-sm text-slate-500">All conversations in one place</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowSettingsDialog(true)}
            data-testid="channel-settings-btn"
          >
            <Settings size={16} className="mr-2" />
            Channels
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchConversations}
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-4 p-3 bg-slate-50 border-b overflow-x-auto">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border">
          <span className="text-xs text-slate-500">Total</span>
          <Badge variant="secondary">{stats.total || 0}</Badge>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border">
          <span className="text-xs text-slate-500">Unread</span>
          <Badge className="bg-red-500">{stats.unread || 0}</Badge>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border">
          <span className="text-xs text-slate-500">Open</span>
          <Badge className="bg-blue-500">{stats.open || 0}</Badge>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border">
          <span className="text-xs text-slate-500">In Progress</span>
          <Badge className="bg-yellow-500">{stats.in_progress || 0}</Badge>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border">
          <span className="text-xs text-slate-500">Resolved</span>
          <Badge className="bg-green-500">{stats.resolved || 0}</Badge>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Conversation List */}
        <div className="w-96 border-r flex flex-col bg-white">
          {/* Filters */}
          <div className="p-3 border-b space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-conversations"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="twitter">X (Twitter)</SelectItem>
                  <SelectItem value="youtube">YouTube</SelectItem>
                  <SelectItem value="website_chat">Website Chat</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Conversation List */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <RefreshCw className="animate-spin text-slate-400" size={24} />
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <MessageSquare className="text-slate-300 mb-3" size={48} />
                <p className="text-slate-500">No conversations yet</p>
                <p className="text-xs text-slate-400 mt-1">
                  Connect channels to start receiving messages
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => fetchConversation(conv.id)}
                    className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedConversation?.id === conv.id ? 'bg-indigo-50 border-l-2 border-indigo-500' : ''
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${channelColors[conv.channel] || 'bg-slate-500'}`}>
                        <ChannelIcon channel={conv.channel} size={14} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-slate-800 truncate">
                            {conv.contact_info?.name || conv.contact_info?.email || 'Unknown'}
                          </span>
                          <span className="text-xs text-slate-400 flex-shrink-0">
                            {formatTime(conv.last_message_at)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">
                          {conv.last_message_preview || conv.subject}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge className={`text-[10px] px-1.5 py-0 ${statusColors[conv.status]}`}>
                            {conv.status?.replace('_', ' ')}
                          </Badge>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">
                              {conv.unread_count}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Conversation Detail */}
        <div className="flex-1 flex flex-col bg-slate-50">
          {selectedConversation ? (
            <>
              {/* Conversation Header */}
              <div className="p-4 bg-white border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${channelColors[selectedConversation.channel] || 'bg-slate-500'}`}>
                    <ChannelIcon channel={selectedConversation.channel} size={18} className="text-white" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-800">
                      {selectedConversation.contact_info?.name || selectedConversation.contact_info?.email || 'Unknown'}
                    </h2>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="capitalize">{selectedConversation.channel?.replace('_', ' ')}</span>
                      {selectedConversation.contact_info?.email && (
                        <>
                          <span>•</span>
                          <span>{selectedConversation.contact_info.email}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Select 
                    value={selectedConversation.status} 
                    onValueChange={handleUpdateStatus}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAssignDialog(true)}
                  >
                    <User size={14} className="mr-1" />
                    Assign
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleCreateLead}
                    data-testid="create-lead-btn"
                  >
                    <UserPlus size={14} className="mr-1" />
                    Create Lead
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4 max-w-3xl mx-auto">
                  {selectedConversation.messages?.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg p-3 ${
                          msg.direction === 'outbound'
                            ? 'bg-indigo-500 text-white'
                            : 'bg-white border shadow-sm'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <div className={`flex items-center gap-2 mt-1 text-xs ${
                          msg.direction === 'outbound' ? 'text-indigo-200' : 'text-slate-400'
                        }`}>
                          <span>{formatTime(msg.timestamp)}</span>
                          {msg.direction === 'outbound' && msg.sender_info?.full_name && (
                            <span>• {msg.sender_info.full_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Message Input */}
              <div className="p-4 bg-white border-t">
                <div className="flex gap-2 max-w-3xl mx-auto">
                  <Textarea
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    className="flex-1 min-h-[44px] max-h-32 resize-none"
                    data-testid="message-input"
                  />
                  <Button 
                    onClick={handleSendMessage} 
                    disabled={!newMessage.trim() || sending}
                    className="bg-indigo-500 hover:bg-indigo-600"
                    data-testid="send-message-btn"
                  >
                    {sending ? (
                      <RefreshCw className="animate-spin" size={18} />
                    ) : (
                      <Send size={18} />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="mx-auto text-slate-300 mb-4" size={64} />
                <h3 className="text-lg font-medium text-slate-600">Select a conversation</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Choose a conversation from the list to view messages
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Assign Conversation</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {agents.map((agent) => (
              <div
                key={agent.id}
                onClick={() => handleAssign(agent.id)}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer border"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-indigo-100 text-indigo-600 text-sm">
                    {agent.full_name?.[0] || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-sm">{agent.full_name || agent.email}</p>
                  <p className="text-xs text-slate-500 capitalize">{agent.role}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Channel Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Channel Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {channels.map((channel) => {
              const Icon = channelIcons[channel.id] || MessageSquare;
              return (
                <div
                  key={channel.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-slate-50"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${channel.enabled ? channelColors[channel.id] : 'bg-slate-200'}`}>
                      <Icon size={20} className={channel.enabled ? 'text-white' : 'text-slate-500'} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{channel.name}</p>
                      <p className="text-xs text-slate-500">{channel.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {channel.configured ? (
                      <Badge className={channel.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                        {channel.enabled ? 'Connected' : 'Disabled'}
                      </Badge>
                    ) : (
                      <Badge variant="outline">Not Configured</Badge>
                    )}
                    <Button variant="outline" size="sm">
                      Configure
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OmnichannelInbox;
